import type { WhatsAppContextField } from '../../models/MasterAdmin/whatsappHub';

/** Full notification/context catalog shown in variable field dropdowns. */
export const ALL_NOTIFICATION_FIELDS: WhatsAppContextField[] = [
  { key: 'patientName', label: 'Patient / recipient name', sourceType: 'context' },
  { key: 'userName', label: 'User name', sourceType: 'context' },
  { key: 'customerName', label: 'Customer name', sourceType: 'context' },
  { key: 'doctorName', label: 'Doctor name', sourceType: 'context' },
  { key: 'vendorName', label: 'Doctor / vendor name', sourceType: 'context' },
  { key: 'name', label: 'Name', sourceType: 'context' },
  { key: 'speciality', label: 'Speciality', sourceType: 'context' },
  { key: 'specialityName', label: 'Speciality name', sourceType: 'context' },
  { key: 'specialty', label: 'Specialty', sourceType: 'context' },
  { key: 'specialityId', label: 'Speciality ID', sourceType: 'context' },
  { key: 'consultationType', label: 'Consultation type', sourceType: 'context' },
  { key: 'categoryName', label: 'Category / consultation type label', sourceType: 'context' },
  { key: 'categoryLabel', label: 'Category / screen label', sourceType: 'context' },
  { key: 'date', label: 'Appointment date', sourceType: 'context' },
  { key: 'time', label: 'Appointment time', sourceType: 'context' },
  { key: 'dateTime', label: 'New date & time', sourceType: 'context' },
  { key: 'oldDateTime', label: 'Old date & time', sourceType: 'context' },
  { key: 'clinicName', label: 'Clinic name', sourceType: 'context' },
  { key: 'clinicAddress', label: 'Clinic address', sourceType: 'context' },
  { key: 'appointmentId', label: 'Appointment ID', sourceType: 'context' },
  { key: 'broadcastId', label: 'Broadcast ID', sourceType: 'context' },
  { key: 'cancelReason', label: 'Cancellation reason', sourceType: 'context' },
  { key: 'cancelledBy', label: 'Cancelled by', sourceType: 'context' },
  { key: 'rescheduledBy', label: 'Rescheduled by', sourceType: 'context' },
  { key: 'customMessage', label: 'Custom message', sourceType: 'context' },
  { key: 'refundAmount', label: 'Refund amount', sourceType: 'context' },
  { key: 'orderId', label: 'Order ID', sourceType: 'context' },
  { key: 'bookingId', label: 'Booking ID', sourceType: 'context' },
  { key: 'phone', label: 'Phone number', sourceType: 'context' },
  { key: 'email', label: 'Email', sourceType: 'context' },
  { key: 'city', label: 'City', sourceType: 'context' },
  { key: 'platform', label: 'App platform', sourceType: 'context' },
  { key: 'userId', label: 'User ID', sourceType: 'context' },
  { key: 'vendorId', label: 'Vendor ID', sourceType: 'context' },
  { key: 'recipientId', label: 'Profile ID', sourceType: 'context' },
  { key: 'vehicleNumber', label: 'Ambulance vehicle number', sourceType: 'context' },
  { key: 'ambulanceType', label: 'Ambulance type', sourceType: 'context' },
  { key: 'driverName', label: 'Driver name', sourceType: 'context' },
  { key: 'driverContact', label: 'Driver contact', sourceType: 'context' },
  { key: 'ambulanceEta', label: 'Ambulance ETA (minutes)', sourceType: 'context' },
  { key: 'ambulanceAgencyName', label: 'Ambulance agency name', sourceType: 'context' },
  { key: 'ambulanceOwnerName', label: 'Ambulance owner name', sourceType: 'context' },
  { key: 'documentName', label: 'Document name', sourceType: 'context' },
  { key: 'ambulanceDocumentName', label: 'Ambulance document name', sourceType: 'context' },
  { key: 'expiryDate', label: 'Document expiry date', sourceType: 'context' },
  { key: 'blogTitle', label: 'Blog title', sourceType: 'context' },
  { key: 'rejectionReason', label: 'Rejection reason', sourceType: 'context' },
  { key: 'vendorLogoutDateTime', label: 'Vendor logout date and time', sourceType: 'context' },
  { key: 'userLogoutDateTime', label: 'User logout date and time', sourceType: 'context' },
  { key: 'acceptDeepLink', label: 'Accept / action deep link', sourceType: 'context' },
  { key: 'deeplinkUrl', label: 'Deep link URL', sourceType: 'context' },
  { key: 'generalLink', label: 'General / consultation retarget link', sourceType: 'context' },
  { key: 'categoryLink', label: 'Consultation category link', sourceType: 'context' },
  { key: 'profileLink', label: 'Doctor profile link', sourceType: 'context' },
  { key: 'resumeLink', label: 'Resume booking link', sourceType: 'context' },
  { key: 'specialtySearchLink', label: 'Specialty search deep link', sourceType: 'context' },
  { key: 'specialtySearchLinkUrl', label: 'Specialty search link URL', sourceType: 'context' },
  { key: 'checkoutLink', label: 'Ambulance checkout link', sourceType: 'context' },
  { key: 'formLink', label: 'Ambulance form link', sourceType: 'context' },
  { key: 'searchLink', label: 'Ambulance search link', sourceType: 'context' },
  { key: 'blogsLink', label: 'Blogs link', sourceType: 'context' },
  { key: 'trackerLink', label: 'Her Phases tracker link', sourceType: 'context' },
  { key: 'uploadLink', label: 'Health record upload link', sourceType: 'context' },
];

/**
 * Same runtime value as another catalog field — keep one canonical key in dropdowns.
 * patientName covers userName/customerName/name; doctorName covers vendorName;
 * speciality covers specialityName/specialty.
 */
const HIDDEN_ALIAS_KEYS = new Set([
  'username',
  'customername',
  'name',
  'vendorname',
  'specialityname',
  'specialtyname',
  'specialty',
  'specialtysearchlinkurl',
  'categoryname',
]);

function normalizeFieldKey(key: string) {
  return String(key || '').trim().toLowerCase();
}

function normalizeFieldLabel(label: string) {
  return String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[/–—-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function fieldGroup(key: string): string {
  if (
    /link|deeplink|url/i.test(key)
  ) {
    return 'Links';
  }
  if (/ambulance|driver|vehicle|eta/i.test(key)) return 'Ambulance';
  if (/blog/i.test(key)) return 'Blogs';
  if (/logout|document|expiry/i.test(key)) return 'Account / documents';
  if (/patient|user|customer|name|phone|email|city|platform|recipient/i.test(key)) {
    return 'Recipient';
  }
  return 'Consultation / booking';
}

export function mergeNotificationFields(
  extra: WhatsAppContextField[] = []
): WhatsAppContextField[] {
  const merged = new Map<string, WhatsAppContextField>();
  for (const f of [...ALL_NOTIFICATION_FIELDS, ...extra]) {
    const key = String(f?.key || '').trim();
    if (!key) continue;
    const norm = normalizeFieldKey(key);
    if (!merged.has(norm)) merged.set(norm, { ...f, key });
  }
  return Array.from(merged.values());
}

export function notificationFieldSelectOptions(
  fields: WhatsAppContextField[],
  selectedKeys: Array<string | null | undefined> = []
) {
  const keepSelected = new Set(
    selectedKeys.map((k) => normalizeFieldKey(String(k || ''))).filter(Boolean)
  );
  const seenKeys = new Set<string>();
  const seenLabels = new Set<string>();
  const options: Array<{ value: string; label: string; hint?: string; group?: string }> = [];

  for (const f of fields) {
    const key = String(f?.key || '').trim();
    if (!key) continue;
    const keyNorm = normalizeFieldKey(key);
    if (seenKeys.has(keyNorm)) continue;
    if (HIDDEN_ALIAS_KEYS.has(keyNorm) && !keepSelected.has(keyNorm)) continue;

    const label = String(f.label || key).trim() || key;
    const labelNorm = normalizeFieldLabel(label);
    if (seenLabels.has(labelNorm) && !keepSelected.has(keyNorm)) continue;

    seenKeys.add(keyNorm);
    seenLabels.add(labelNorm);
    options.push({
      value: key,
      label,
      hint: key,
      group: fieldGroup(key),
    });
  }

  return options;
}
